import { Component } from 'react';

import { MetricEventName } from '../../../types/metric-events';
import { emitStudio } from '../state/studio-events';

interface StudioNudgeProps {
    /** How many still images were picked. Carried on all four events (P5a.6's attribute map). */
    files: number;
    /** Open the Studio. The owner writes the URL; this only asks. */
    onAccept: () => void;
    /** The user said no. The banner goes away for the rest of the session. */
    onDismiss: () => void;
}

/**
 * "You picked N photos — want to make a video?" (§6.1, §8's NUDGE_* funnel).
 *
 * A BANNER, not a toast: a toast auto-dismisses after a few seconds
 * (`services/toast.service.ts`'s own default), which would make NUDGE_DISMISSED meaningless —
 * most dismissals would be the timer, and the acceptance ratio (§8's 15–35 % guardrail) would be
 * measuring how fast people read.
 *
 * `role="status"` rather than `role="alert"`: an offer is not an interruption, and a screen
 * reader that cuts across the upload announcement to read it is a worse page than one that does
 * not offer at all.
 *
 * The four events are raised where they happen and nowhere else:
 *   SHOWN     on mount, once per mounted banner;
 *   ACCEPTED  on the CTA;
 *   DISMISSED on the close control;
 *   DECLINED  by the OWNER, when the user converts with the banner still on screen -- which is
 *             the only honest reading of "declined", and is why it is not raised here.
 *
 * This file takes no import edge into `components/studio/views|media|model`: it is mounted by
 * `file-convert.component.tsx`, which is in the static upload graph (D10). React, an enum and the
 * bound emitter, and nothing else.
 *
 * WHICH IS WHY THE COPY IS HERE AND NOT IN `views/studio.copy.ts`, the one deliberate exception to
 * "every copy string lives in studio.copy.ts". That module sits in `views/`, which reaches
 * `model/limits` (COPY derives its numbers from `STUDIO_LIMITS`) and from there the rest of the
 * editor -- exactly the edge D10 forbids in this graph, and the same layering reason
 * `services/toast.service.ts` keeps `UNDOABLE_CLOSE_LABEL` rather than importing COPY. The banner
 * is also the only Studio surface a user sees while the Studio itself has not loaded, so its words
 * cannot depend on the Studio's chunk being present.
 */
export class StudioNudgeComponent extends Component<StudioNudgeProps> {
    componentDidMount() {
        emitStudio(MetricEventName.NUDGE_SHOWN, { files: this.props.files });
    }

    private onAccept = () => {
        emitStudio(MetricEventName.NUDGE_ACCEPTED, { files: this.props.files });
        this.props.onAccept();
    };

    private onDismiss = () => {
        emitStudio(MetricEventName.NUDGE_DISMISSED, { files: this.props.files });
        this.props.onDismiss();
    };

    render() {
        const { files } = this.props;
        return (
            <div
                className="alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2"
                role="status"
                data-testid="studio-nudge"
            >
                <span>
                    {files} photos — want to turn them into a video with timing, music and transitions?
                </span>
                <span className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        data-testid="studio-nudge-accept"
                        onClick={this.onAccept}
                    >
                        Open the Studio
                    </button>
                    <button
                        type="button"
                        className="btn btn-link btn-sm"
                        data-testid="studio-nudge-dismiss"
                        onClick={this.onDismiss}
                        aria-label="Dismiss the Studio offer"
                    >
                        No thanks
                    </button>
                </span>
            </div>
        );
    }
}

export default StudioNudgeComponent;
